import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TimezoneCombobox } from "@/components/TimezoneCombobox";
import { Check, ChevronsUpDown, Loader2, PartyPopper, Pencil, Plus, Trash2 } from "lucide-react";

const hourLabel = (h) => {
  const twelve = ((h + 11) % 12) + 1;
  return `${twelve}:00 ${h < 12 ? "AM" : "PM"}`;
};

const regionName = (code) => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code;
  } catch (e) {
    return code;
  }
};

const HolidayCountryCombobox = ({ value, onChange, countries, disabled }) => {
  const [open, setOpen] = useState(false);
  const options = useMemo(
    () => countries.map((c) => ({ code: c, name: regionName(c) })).sort((a, b) => a.name.localeCompare(b.name)),
    [countries]
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" disabled={disabled}
          className="w-full justify-between rounded-lg font-normal" data-testid="holiday-country-combobox-trigger">
          <span className="flex items-center gap-2 truncate">
            <PartyPopper className="h-4 w-4 text-muted-foreground shrink-0" />
            {value ? `${regionName(value)} (${value})` : "No holiday calendar"}
          </span>
          <ChevronsUpDown className="h-4 w-4 text-muted-foreground shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search countries…" data-testid="holiday-country-search-input" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="none no-holiday-calendar" onSelect={() => { onChange(null); setOpen(false); }}>
                <Check className={`mr-2 h-4 w-4 ${!value ? "opacity-100" : "opacity-0"}`} />
                No holiday calendar
              </CommandItem>
              {options.map((o) => (
                <CommandItem key={o.code} value={`${o.name} ${o.code}`} onSelect={() => { onChange(o.code); setOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 ${o.code === value ? "opacity-100" : "opacity-0"}`} />
                  {o.name} <span className="ml-1 text-muted-foreground">({o.code})</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const OrgTab = () => {
  const { user, refreshUser } = useAuth();
  const canManage = ["owner", "admin"].includes(user?.role);
  const [org, setOrg] = useState(null);
  const [categories, setCategories] = useState(null);
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState({ name: "", default_threshold_days: 7, timezone: "UTC", digest_hour: 8, holiday_country: null });
  const [saving, setSaving] = useState(false);
  const [catDialog, setCatDialog] = useState({ open: false, editing: null, name: "", threshold: "" });
  const [catBusy, setCatBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    const [orgRes, catsRes] = await Promise.all([api.get("/org"), api.get("/categories")]);
    setOrg(orgRes.data);
    setForm({
      name: orgRes.data.name,
      default_threshold_days: orgRes.data.default_threshold_days,
      timezone: orgRes.data.timezone,
      digest_hour: orgRes.data.digest_hour ?? 8,
      holiday_country: orgRes.data.holiday_country || null,
    });
    setCategories(catsRes.data.categories);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canManage) return;
    api.get("/meta/holiday-countries").then((res) => setCountries(res.data.countries)).catch(() => {});
  }, [canManage]);

  const saveOrg = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        default_threshold_days: Number(form.default_threshold_days),
        timezone: form.timezone,
        digest_hour: Number(form.digest_hour),
      };
      if (form.holiday_country) payload.holiday_country = form.holiday_country;
      else payload.clear_holiday_country = true;
      await api.patch("/org", payload);
      toast.success("Organization settings saved.");
      refreshUser();
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const openCatDialog = (cat = null) => {
    setCatDialog({
      open: true,
      editing: cat,
      name: cat?.name || "",
      threshold: cat?.threshold_days ?? "",
    });
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    setCatBusy(true);
    try {
      const payload = {
        name: catDialog.name.trim(),
        threshold_days: catDialog.threshold === "" ? null : Number(catDialog.threshold),
      };
      if (catDialog.editing) {
        if (payload.threshold_days === null) payload.clear_threshold = true;
        await api.patch(`/categories/${catDialog.editing.id}`, payload);
        toast.success("Category updated.");
      } else {
        await api.post("/categories", payload);
        toast.success("Category added.");
      }
      setCatDialog({ open: false, editing: null, name: "", threshold: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setCatBusy(false);
    }
  };

  const deleteCategory = async () => {
    try {
      await api.delete(`/categories/${deleteTarget.id}`);
      toast.success("Category deleted.");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
      setDeleteTarget(null);
    }
  };

  if (!org || !categories) {
    return <div className="space-y-6"><Skeleton className="h-64" /><Skeleton className="h-64" /></div>;
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Organization defaults</CardTitle>
          <CardDescription>
            {canManage ? "These apply to every item unless a category or item overrides them." : "Only owners and admins can change these settings."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveOrg} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization name</Label>
              <Input id="org-name" required maxLength={100} value={form.name} disabled={!canManage}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="org-name-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="org-threshold">Default reminder window (days)</Label>
                <Input id="org-threshold" type="number" min={1} max={365} required disabled={!canManage}
                  value={form.default_threshold_days}
                  onChange={(e) => setForm({ ...form, default_threshold_days: e.target.value })}
                  data-testid="org-threshold-input" />
                <p className="text-xs text-muted-foreground">We'll include items in the daily summary email this many days before their due date.</p>
              </div>
              <div className="space-y-2">
                <Label>Daily summary time</Label>
                <Select value={String(form.digest_hour)} onValueChange={(v) => setForm({ ...form, digest_hour: Number(v) })} disabled={!canManage}>
                  <SelectTrigger className="rounded-lg" data-testid="org-digest-hour-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {Array.from({ length: 24 }, (_, h) => (
                      <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Owners also get a Monday snapshot of the week ahead at this time.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Timezone</Label>
                <TimezoneCombobox value={form.timezone} disabled={!canManage}
                  onChange={(tz) => setForm({ ...form, timezone: tz })} />
                <p className="text-xs text-muted-foreground">Due dates and email times use this timezone.</p>
              </div>
              <div className="space-y-2">
                <Label>Public holidays</Label>
                <HolidayCountryCombobox value={form.holiday_country} countries={countries} disabled={!canManage}
                  onChange={(code) => setForm({ ...form, holiday_country: code })} />
                <p className="text-xs text-muted-foreground">Shown on the calendar so renewals don't land on a holiday.</p>
              </div>
            </div>
            {canManage && (
              <Button type="submit" disabled={saving} className="rounded-xl" data-testid="org-settings-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Categories</CardTitle>
            <CardDescription className="mt-1.5">
              Group items by type. A category can set its own reminder window.
            </CardDescription>
          </div>
          {canManage && (
            <Button variant="secondary" size="sm" className="rounded-lg" onClick={() => openCatDialog()} data-testid="category-add-button">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <ul className="divide-y divide-border" data-testid="categories-list">
            {categories.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-3 gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {c.threshold_days !== null && c.threshold_days !== undefined
                      ? `Reminds ${c.threshold_days} days before`
                      : "Uses organization default"}
                    {" · "}{c.item_count} item{c.item_count !== 1 ? "s" : ""}
                  </p>
                </div>
                {canManage && (
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Edit ${c.name}`}
                      onClick={() => openCatDialog(c)} data-testid={`category-edit-${c.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--destructive))]"
                      aria-label={`Delete ${c.name}`} onClick={() => setDeleteTarget(c)} data-testid={`category-delete-${c.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Dialog open={catDialog.open} onOpenChange={(o) => setCatDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{catDialog.editing ? "Edit category" : "Add a category"}</DialogTitle>
            <DialogDescription>Categories keep similar items together, like “Work Permit” or “Fleet Insurance”.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveCategory} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cat-name">Name</Label>
              <Input id="cat-name" required maxLength={60} value={catDialog.name}
                onChange={(e) => setCatDialog((d) => ({ ...d, name: e.target.value }))}
                data-testid="category-form-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-threshold">Reminder window (days, optional)</Label>
              <Input id="cat-threshold" type="number" min={0} max={365} value={catDialog.threshold}
                onChange={(e) => setCatDialog((d) => ({ ...d, threshold: e.target.value }))}
                placeholder="Leave blank to use the organization default"
                data-testid="category-form-threshold-input" />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="ghost" className="rounded-xl"
                onClick={() => setCatDialog({ open: false, editing: null, name: "", threshold: "" })}>Cancel</Button>
              <Button type="submit" disabled={catBusy} className="rounded-xl" data-testid="category-form-submit-button">
                {catBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : catDialog.editing ? "Save" : "Add category"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.item_count > 0
                ? `This category is used by ${deleteTarget.item_count} item(s) — you'll need to move or delete those first.`
                : "This can't be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteCategory} className="rounded-xl bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90" data-testid="category-delete-confirm-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
